import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../Button';
import Form from '../Form';
import Loader from '../Loader';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { selectValidation } from 'src/store/selectors/Validation';
import { getUserProfileDetails } from 'src/store/actions/settings';
import { clearValidation } from 'src/store/actions/validation';
import { upload } from 'src/helpers/upLoadHelper';
import {
  IRawNoteFormData,
  NoteAppointment,
} from 'src/SharedCustomHooks/UseNotesAPI/types';
import NoteFormFields from './NoteFormFields';

interface NoteFormProps {
  onClose: () => void;
  breadcrumb: string;
  from: string;
  onSaveNote: any;
  onEditNote: any;
  initialNote?: any;
}

const NoteFormWithState = ({
  onClose,
  breadcrumb,
  from,
  onSaveNote,
  onEditNote,
  initialNote = null,
}: NoteFormProps) => {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthenticatedUser);
  const loader = useSelector(selectFormLoader);
  const validation = useSelector(selectValidation);

  const handleGetUserProfileDetails = (userId: string) =>
    dispatch(getUserProfileDetails(userId));
  const handleClearValidation = () => dispatch(clearValidation());

  const [formData, setFormData] = useState<IRawNoteFormData>({
    id: initialNote?.id || null,
    title: initialNote?.title || '',
    body: initialNote?.body || '',
    visibilityType: initialNote?.visibilityType || 'Everyone',
    noteVisibleToHomeownerOccupantRealtor:
      initialNote?.noteVisibleToHomeownerOccupantRealtor || false,
    appointment: initialNote?.appointment || null,
    image: initialNote?.image || null,
  });

  const [saveButtonClicked, setSaveButtonClicked] = useState(false);
  const [editorState, setEditorState] = useState(initialNote?.body || '');
  const [buttonPressed, setButtonPressed] = useState('');

  useEffect(() => {
    if (initialNote) {
      setFormData({
        id: initialNote.id,
        title: initialNote.title || '',
        body: initialNote.body || '',
        visibilityType: initialNote.visibilityType || 'Everyone',
        noteVisibleToHomeownerOccupantRealtor:
          initialNote.noteVisibleToHomeownerOccupantRealtor || false,
        appointment: initialNote.appointment || null,
        image: initialNote.image || null,
      });

      setEditorState(initialNote.body || '');
    }
  }, [initialNote]);

  useEffect(() => {
    if (validation.status === true) {
      if (
        validation.message === 'FaqDocNotes' &&
        buttonPressed === 'SaveAndClose'
      ) {
        onClose();
        handleClearValidation();
      } else if (
        validation.message === 'FaqDocNotes' &&
        buttonPressed === 'SaveAndAdd'
      ) {
        handleClearValidation();
        setEditorState('');
        setFormData({
          id: null,
          title: '',
          body: '',
          visibilityType: 'Everyone',
          noteVisibleToHomeownerOccupantRealtor: false,
          appointment: null,
          image: null,
        });
      }
    }
  }, [validation, buttonPressed, onClose]);

  const onEditorChange = (newEditorState: string) => {
    setEditorState(newEditorState);
    setFormData((prev: IRawNoteFormData) => ({
      ...prev,
      body: newEditorState,
    }));
  };

  const updateFormData = (updates: Partial<IRawNoteFormData>) => {
    setFormData((prev: IRawNoteFormData) => ({
      ...prev,
      ...updates,
    }));
  };

  const prepareNote = async (): Promise<IRawNoteFormData> => {
    let imageUrl = null;

    // Todo: Migrate the image upload logic to the useNotesAPI custom hook.
    try {
      if (
        formData.image &&
        Array.isArray(formData.image) &&
        formData.image.length === 1
      ) {
        setSaveButtonClicked(true);
        imageUrl = await upload(formData.image[0]);
        setTimeout(() => setSaveButtonClicked(false), 3000);
      }

      return {
        visibilityType: formData.visibilityType,
        noteVisibleToHomeownerOccupantRealtor:
          formData.noteVisibleToHomeownerOccupantRealtor,
        title: formData.title,
        body: formData.body,
        image: imageUrl,
        appointment: formData.appointment,
        author: {
          id: user.id,
          firstName: user.firstname,
          lastName: user.lastname,
        },
        createdAt: new Date(),
      };
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const onHandleNoteSubmit = async (note: IRawNoteFormData) => {
    const noteToAction: IRawNoteFormData = await prepareNote();

    if (note.id) {
      onEditNote({ ...noteToAction, id: note.id });
    } else {
      onSaveNote(noteToAction);
    }
  };

  const buttonDisabled = () => {
    const hasAppointment =
      formData.appointment &&
      typeof formData.appointment === 'object' &&
      'date' in formData.appointment &&
      'time' in formData.appointment;

    if (hasAppointment) {
      const appointment = formData.appointment as NoteAppointment;
      return (
        !formData.title ||
        !formData.body ||
        !appointment.date ||
        !appointment.time
      );
    } else {
      return !formData.title || !formData.body;
    }
  };

  return (
    <Form
      headline={(formData.id ? 'Edit' : 'Add') + ' a Note'}
      onClose={onClose}
      onSubmit={() => onHandleNoteSubmit(formData)}
      actions={
        <>
          {loader && <Loader center={true} />}
          {!formData.id && (
            <Button
              color="semantic"
              size="small"
              label="Save and Add Another"
              onClick={() => {
                setButtonPressed('SaveAndAdd');
                updateFormData({ title: '' });
              }}
              disabled={buttonDisabled()}
            />
          )}
          <Button
            color="teal"
            size="small"
            label="Save Note"
            onClick={() => setButtonPressed('SaveAndClose')}
            disabled={buttonDisabled() || saveButtonClicked}
          />
        </>
      }
      component={
        <NoteFormFields
          formData={formData}
          editorState={editorState}
          breadcrumb={breadcrumb}
          from={from}
          onFormDataChange={updateFormData}
          onEditorChange={onEditorChange}
          onGetUserProfileDetails={handleGetUserProfileDetails}
          userId={user.id}
        />
      }
      footer={undefined}
      strapline={undefined}
      onExit={undefined}
    />
  );
};

export default NoteFormWithState;
